import { useCallback } from 'react';
import { useAgaveClientUtils } from 'app/pages/overview/tabs/transactions/AgaveTransactions/utils';

type UseGetOptionLabelProps = {
  isQBOClient?: boolean;
  isQBDClient?: boolean;
  identifierField?: string | null;
  identifierOrder?: 'before' | 'after' | null;
  nameField?: string;
  includeDescription?: boolean;
};

export const defaultIdentifierOrder = 'after'; // for now, should be one of: `before`, `after`

export function formatWithIdentifier(
  option: any,
  identifierField: string | null,
  identifierOrder: 'before' | 'after' | null = defaultIdentifierOrder,
  nameField = 'name'
): string {
  if (!option) return '';

  if (!identifierField || !(identifierField in option)) return option[nameField] || '';

  const identifierValue = option[identifierField];
  const nameValue = option[nameField];

  // If identifier value is null, undefined, or empty, just return the name
  if (!identifierValue) return nameValue || '';
  // SEE-1325: and the mirror case — a code with no name. Vista equipment and EM
  // rows frequently have no description (the reason equipment sorts by code),
  // and these used to be labelled by callers that fell back to the bare code.
  // Without this the template renders the literal string "null".
  if (!nameValue) return String(identifierValue);

  if (identifierOrder === 'before') {
    // SEE-1325: parenthesised like the 'after' form. Vista codes end in their
    // own punctuation ("2- -"), so a bare prefix runs three dashes together —
    // "2- - - New job phase". The brackets keep the code readable as one unit.
    return `(${identifierValue}) - ${nameValue}`;
  } else if (identifierOrder === 'after') {
    return `${nameValue} - (${identifierValue})`;
  } else {
    return nameValue || '';
  }
}

const includeSuffixField = (label: string, option: any, field: string, separator = ' '): string => {
  if (!option[field]) return label;
  return `${label}${separator}${option[field].trim()}`;
};

export const useGetOptionLabel = ({
  isQBOClient,
  isQBDClient,
  identifierField = null,
  identifierOrder = defaultIdentifierOrder,
  nameField = 'name',
  includeDescription = false,
}: UseGetOptionLabelProps) => {
  const { extractFullNameFromSourceData, extractFullyQualifiedNameFromSourceData } = useAgaveClientUtils();

  return useCallback(
    (option: any) => {
      if (!option) return '';

      let label: string;

      if (isQBOClient) {
        label = extractFullyQualifiedNameFromSourceData(option).FullyQualifiedName || option[nameField] || '';
      } else if (isQBDClient) {
        label = extractFullNameFromSourceData(option).FullName || option[nameField] || '';
      } else if (identifierField && identifierField in option) {
        label = formatWithIdentifier(option, identifierField, identifierOrder, nameField);
      } else {
        label = option[nameField] || '';
      }
      if (includeDescription) label = includeSuffixField(label, option, 'description', ' - ');
      return label;
    },
    [
      extractFullNameFromSourceData,
      extractFullyQualifiedNameFromSourceData,
      isQBDClient,
      isQBOClient,
      identifierField,
      identifierOrder,
      nameField,
      includeDescription,
    ]
  );
};
